import { formatDateTime } from '@utils/timeFormatter';
import { memo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner';
import { CheckCircle, WarningIcon } from '../../../../components/Icons';

export type MemoryStatus = 'idle' | 'processing' | 'error' | 'none';

interface IMemoryItemCardProps {
  icon?: ReactNode;
  title: string;
  status?: MemoryStatus;
  onDelete?: () => void;
  onRetry?: () => void;
  deleteDisabled?: boolean;
  createdAt?: string;
  oneLine?: boolean;
  hideDelete?: boolean;
}

const MemoryItemCard: React.FC<IMemoryItemCardProps> = memo(
  ({
    icon,
    title,
    status = 'idle',
    onDelete,
    onRetry,
    createdAt,
    deleteDisabled = false,
    oneLine = false,
    hideDelete = false,
  }) => {
    const { t } = useTranslation();
    const renderStatus = () => {
      switch (status) {
        case 'processing':
          return <AnimatedSpinner size={16} />;
        case 'error':
          return (
            <button
              onClick={onRetry}
              className="text-red-500 flex items-center gap-1">
              {t('twin.retry')}
              <WarningIcon width={16} />
            </button>
          );
        case 'idle':
          return (
            <div className="w-2 h-2 rounded-full flex items-center justify-center bg-white overflow-visible">
              <div className="text-green-200">
                <CheckCircle size={16} />
              </div>
            </div>
          );
        default:
          return <div className="w-[17px]" />;
      }
    };

    return (
      <div className="tc-mv-blur px-4 py-3 w-full ">
        {createdAt && (
          <div className="text-[10px] text-white/50">
            {formatDateTime(createdAt)}
          </div>
        )}
        <div className="flex gap-2 items-center">
          {icon && icon}
          <div className={`flex-1 ${oneLine ? 'truncate' : ''}`} title={title}>
            {title}
          </div>
          <div className="flex items-center justify-center ms-auto">
            {renderStatus()}
          </div>
          {!hideDelete && (
            <button
              onClick={onDelete}
              className="ms-2"
              disabled={deleteDisabled}>
              <i className="fa-solid fa-trash" />
            </button>
          )}
        </div>
      </div>
    );
  },
);

export { MemoryItemCard };
